import { useState, useEffect, useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { useDisclosure } from '@mantine/hooks'
import { Plus } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_PACKAGE_ORDERS,
  GET_PACKAGE_ORDER_ENHANCED_REPORT,
} from 'src/graphql/packageOrders.graphql'

import RequestOrderModal from '../DocumentsTab/components/DQFContent/components/RequestOrderModal'

import OrdersTable from './components/OrdersTable'
import OrderDetails from './OrderDetails'

const OrdersTab = ({ applicantId, onRender }) => {
  const [
    orderDetailsOpened,
    { open: openOrderDetails, close: closeOrderDetails },
  ] = useDisclosure(false)
  const [selectedPackageOrder, setSelectedPackageOrder] = useState(null)
  const [loadingEnhancedReport, setLoadingEnhancedReport] = useState(null)
  const [
    requestOrderModalOpened,
    { open: openRequestOrderModal, close: closeRequestOrderModal },
  ] = useDisclosure(false)

  const [getPackageOrders, { data: packageOrdersData, loading }] = useLazyQuery(
    GET_PACKAGE_ORDERS,
    {
      onError: () => {
        toast('Failed to load package orders', 'error')
      },
    }
  )

  const handleRequestOrderModalClose = () => {
    closeRequestOrderModal()
    // Table will automatically reset to page 1 when new data arrives
  }

  const handleRowClick = (rowData) => {
    setSelectedPackageOrder(rowData)
    openOrderDetails()
  }

  const handleLoadPage = useCallback(
    ({ offset, limit }) => {
      getPackageOrders({
        variables: {
          filters: {
            applicantId,
          },
          pageInfo: {
            limit,
            offset,
          },
        },
      })
    },
    [getPackageOrders, applicantId]
  )

  // Load initial data
  useEffect(() => {
    if (applicantId) {
      handleLoadPage({ offset: 0, limit: 20 })
    }
  }, [applicantId, handleLoadPage])

  const [getEnhancedReport] = useLazyQuery(GET_PACKAGE_ORDER_ENHANCED_REPORT, {
    onCompleted: (data) => {
      if (data?.getPackageOrderEnhancedReport?.url) {
        window.open(data.getPackageOrderEnhancedReport.url, '_blank')
      }
      setLoadingEnhancedReport(null)
    },
    onError: () => {
      toast('Failed to generate enhanced report', 'error')
      setLoadingEnhancedReport(null)
    },
  })

  const handleEnhancedReportClick = (packageOrderId) => {
    setLoadingEnhancedReport(packageOrderId)
    getEnhancedReport({
      variables: {
        packageOrderId,
      },
    })
  }

  const packageOrders = packageOrdersData?.getPackageOrders?.items || []
  const totalCount = packageOrdersData?.getPackageOrders?.totalCount || 0

  return (
    <div className="flex h-full flex-col gap-6 pb-8">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-doubleNickel-black">
              Package Orders
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              {/* These spans are necessary to prevent Google Translate issues  */}
              <span>{totalCount}</span>
              <span> order(s)</span>
            </div>
          </div>
          <DNButton variant="primary" onClick={openRequestOrderModal}>
            <Plus size={16} className="stroke-white" />
            Request
          </DNButton>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <OrdersTable
          loading={loading}
          orders={packageOrders}
          loadingEnhancedReportId={loadingEnhancedReport}
          onEnhancedReportClick={handleEnhancedReportClick}
          onRowClick={(order) => handleRowClick(order)}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
        />
      </div>

      <RequestOrderModal
        refetchQueries={[...onRender, getOperationName(GET_PACKAGE_ORDERS)]}
        isModalOpen={requestOrderModalOpened}
        onModalClose={handleRequestOrderModalClose}
        applicantId={applicantId}
      />

      <OrderDetails
        applicantId={applicantId}
        packageOrder={selectedPackageOrder}
        opened={orderDetailsOpened}
        onClose={closeOrderDetails}
      />
    </div>
  )
}

export default OrdersTab
